import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, AlertTriangle, CheckCircle, Users, CreditCard } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-muted-foreground">Last updated: February 2026</p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4">Agreement to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using ScanIt2Go's services, you agree to be bound by these Terms of Service. 
              If you disagree with any part of these terms, you may not access our services. These terms 
              apply to all users, visitors, and others who access or use our NFC tag management platform.
            </p>
          </section>

          {/* Service Description */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" />
              Service Description
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              ScanIt2Go provides a platform for creating, managing, and distributing digital content 
              linked to NFC tags and QR codes. Our services include:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>NFC tag content management and hosting</li>
              <li>QR code generation and customization</li>
              <li>Media file storage (images, videos, audio)</li>
              <li>Analytics and tracking of tag scans</li>
              <li>White-label solutions for businesses</li>
              <li>Gift and personal message delivery</li>
            </ul>
          </section>

          {/* User Responsibilities */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              User Responsibilities
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Account Security</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>You are responsible for maintaining the confidentiality of your admin password</li>
                  <li>You must notify us immediately of any unauthorized access</li>
                  <li>You are responsible for all activities under your account</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Acceptable Use</h3>
                <p className="text-muted-foreground mb-2">You agree NOT to use our services to:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Upload illegal, harmful, or offensive content</li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe on intellectual property rights</li>
                  <li>Distribute malware, viruses, or harmful code</li>
                  <li>Harass, abuse, or harm others</li>
                  <li>Spam or send unsolicited communications</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Content Guidelines */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary" />
              Content Guidelines
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Your Content</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>You retain ownership of content you upload</li>
                  <li>You grant us a license to host and display your content</li>
                  <li>You are solely responsible for content you upload</li>
                  <li>We may remove content that violates these terms</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Prohibited Content</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Illegal content or content promoting illegal activities</li>
                  <li>Sexually explicit or pornographic material</li>
                  <li>Violent or threatening content</li>
                  <li>Hate speech or discriminatory content</li>
                  <li>Content that exploits minors</li>
                  <li>Copyrighted material without permission</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Payment Terms (if applicable) */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Payment Terms
            </h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Prices for products and services are subject to change</li>
              <li>All purchases are final unless otherwise stated</li>
              <li>Refunds may be issued at our discretion</li>
              <li>You agree to provide accurate billing information</li>
              <li>Subscription services may be cancelled at any time</li>
            </ul>
          </section>

          {/* Limitations */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Limitations of Liability
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Service Availability</h3>
                <p className="text-muted-foreground">
                  We strive to provide reliable services but do not guarantee uninterrupted access. 
                  Services may be temporarily unavailable for maintenance or due to circumstances 
                  beyond our control.
                </p>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Disclaimer</h3>
                <p className="text-muted-foreground">
                  Our services are provided "as is" without warranties of any kind. We are not 
                  liable for any indirect, incidental, or consequential damages arising from 
                  your use of our services.
                </p>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Indemnification</h3>
                <p className="text-muted-foreground">
                  You agree to indemnify and hold harmless ScanIt2Go from any claims, damages, 
                  or expenses arising from your use of our services or violation of these terms.
                </p>
              </div>
            </div>
          </section>

          {/* Termination */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4">Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may terminate or suspend your access immediately, without prior notice, for any 
              reason including breach of these Terms. Upon termination, your right to use our 
              services will cease immediately. Content associated with terminated accounts may 
              be deleted.
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4">Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify users of 
              significant changes by posting the new terms on this page and updating the "last 
              updated" date. Continued use of our services after changes constitutes acceptance 
              of the new terms.
            </p>
          </section>

          {/* Governing Law */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4">Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These terms shall be governed by and construed in accordance with the laws of the 
              jurisdiction in which ScanIt2Go operates, without regard to conflict of law provisions.
            </p>
          </section>

          {/* Contact */}
          <section className="rounded-2xl border border-border/40 bg-card/50 p-6">
            <h2 className="text-xl font-semibold mb-4">Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="space-y-2 text-muted-foreground">
              <p>Email: <a href="mailto:legal@scanit2go.com" className="text-primary hover:underline">legal@scanit2go.com</a></p>
              <p>Website: <Link to="/" className="text-primary hover:underline">www.scanit2go.com</Link></p>
            </div>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-border/40 flex flex-wrap gap-4 text-sm text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          <span>•</span>
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <span>•</span>
          <Link to="/faq" className="hover:text-foreground transition-colors">FAQ</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
